/**
 * LeoLibros MASTER5-COMP-C1 R3
 * Product recommendation orchestration for PDP.
 *
 * Top block priority:
 *   1. Shopify complementary recommendations
 *   2. Shopify related recommendations only when complementary returns no renderable cards
 *   3. Remove the top block when both are empty
 *
 * Native lower "Títulos relacionados":
 *   - preserved as a second discovery layer
 *   - duplicate products already shown in "Te gustaría" are removed
 *   - up to the section's configured visible count are retained
 *
 * This is intentionally isolated from Prestige's core theme.js.
 */
class LLProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.llLoaded === 'true') return;
    this.dataset.llLoaded = 'true';
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const productId = this.getAttribute('product');
    const sectionId = this.getAttribute('section-id');
    const complementaryLimit = this.getAttribute('limit') || '2';
    const relatedLimit = this.getAttribute('fallback-limit') || '4';

    if (!productId || !sectionId) {
      this.remove();
      return;
    }

    try {
      const complementary = await this.fetchRecommendationSection('complementary', complementaryLimit);

      if (!this.isConnected) return;

      if (complementary) {
        this.renderFrom(complementary, 'complementary');
        return;
      }

      const related = await this.fetchRecommendationSection('related', relatedLimit);

      if (!this.isConnected) return;

      if (related) {
        this.renderFrom(related, 'related');
        return;
      }

      this.remove();
    } catch (error) {
      console.warn('[LeoLibros] Recommendation fallback unavailable.', error);
      this.remove();
    }
  }

  async fetchRecommendationSection(intent, limit) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      product_id: this.getAttribute('product'),
      limit: String(limit),
      section_id: this.getAttribute('section-id'),
      intent
    });

    const response = await fetch(`${root}recommendations/products?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Recommendation request failed (${response.status})`);
    }

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const recommendationElement = documentFragment.querySelector('ll-product-recommendations');

    if (!recommendationElement || recommendationElement.childElementCount === 0) {
      return null;
    }

    return recommendationElement;
  }

  renderFrom(sourceElement, intent) {
    const handles = this.collectProductHandles(sourceElement);

    this.replaceChildren(...document.importNode(sourceElement, true).childNodes);
    this.dataset.recommendationIntent = intent;
    this.dataset.recommendationHandles = JSON.stringify(handles);
    this.hidden = false;

    this.prepareResponsivePlacement();
    this.prepareAndDedupeNativeRelated(handles);
  }

  prepareResponsivePlacement() {
    // R3: preserve the approved desktop position, but on narrow screens move
    // "Te gustaría" after the editorial product content so it does not interrupt
    // Por qué leerlo / Reseña / Ficha / Explora más.
    if (!this._llPlacementPlaceholder) {
      this._llPlacementPlaceholder = document.createComment('ll-product-recommendations-original-position');
      this.parentNode?.insertBefore(this._llPlacementPlaceholder, this);
    }

    const media = window.matchMedia('(max-width: 999px)');

    const applyPlacement = () => {
      if (!this.isConnected) return;

      if (media.matches) {
        const section = this.closest('.shopify-section');
        const extraInformation = section?.querySelector('#product-extra-information');
        const product = section?.querySelector('.product');

        if (extraInformation) {
          extraInformation.insertAdjacentElement('afterend', this);
        } else if (product && this.parentElement !== product) {
          product.append(this);
        }
      } else {
        const placeholder = this._llPlacementPlaceholder;
        if (placeholder?.isConnected && placeholder.parentNode) {
          placeholder.parentNode.insertBefore(this, placeholder.nextSibling);
        }
      }
    };

    applyPlacement();

    if (!this._llPlacementMedia) {
      this._llPlacementMedia = media;
      this._llPlacementHandler = applyPlacement;
      media.addEventListener?.('change', applyPlacement);
    }
  }

  collectProductHandles(root) {
    const handles = new Set();

    root.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const handle = this.handleFromProductUrl(link.getAttribute('href'));
      if (handle) handles.add(handle);
    });

    return [...handles];
  }

  handleFromProductUrl(url) {
    if (!url) return null;

    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_error) {
      const match = String(url).match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    }
  }

  prepareAndDedupeNativeRelated(topHandles) {
    const topHandleSet = new Set(topHandles);
    let observer;

    const apply = () => {
      const related = document.querySelector('product-recommendations[data-ll-related-dedupe]');
      if (!related) return false;

      // Keep the lower native layer hidden until de-duplication is complete.
      related.style.visibility = 'hidden';

      const cards = [...related.querySelectorAll('product-card[handle]')];
      if (cards.length === 0) return false;

      const visibleLimit = Math.max(1, Number.parseInt(related.dataset.llDisplayLimit || '4', 10) || 4);
      const seen = new Set();
      let kept = 0;

      cards.forEach((card) => {
        const handle = card.getAttribute('handle');
        const duplicateTop = !handle || topHandleSet.has(handle);
        const duplicateLower = handle && seen.has(handle);
        const beyondLimit = kept >= visibleLimit;

        if (duplicateTop || duplicateLower || beyondLimit) {
          card.remove();
          return;
        }

        seen.add(handle);
        kept += 1;
      });

      const section = related.closest('.shopify-section--related-products');

      if (kept === 0) {
        if (section) section.hidden = true;
        return true;
      }

      if (section) section.hidden = false;
      related.style.visibility = '';
      related.dataset.llDeduped = 'true';
      related.dataset.llVisibleCount = String(kept);
      return true;
    };

    // The related section can be parsed/loaded before or after the top block.
    // Try now, then watch the document until native Shopify recommendations arrive.
    if (apply()) return;

    observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Do not retain an observer forever on pages where Shopify returns no related products.
    window.setTimeout(() => observer.disconnect(), 15000);
  }
}

if (!window.customElements.get('ll-product-recommendations')) {
  window.customElements.define('ll-product-recommendations', LLProductRecommendations);
}
